/**
 * Being shown inside BRUNT HQ's frame, from the browser's side.
 *
 * HQ opens this app in an iframe at `/apps/external`. Inside it, a sign-in
 * cannot happen where the page is: Google's sign-in page refuses to be framed,
 * and a frame may only move the whole tab while handling a click. So when the
 * session ends inside HQ, "Sign in again" sends the whole tab through
 * `/api/signin` and then {@link WARM_PATH}, which lands back in HQ's frame on
 * the same page.
 *
 * The server half, and HQ's own pre-warm check, are in the backend's
 * `app/core/embedding.py`.
 */

/** The API's hop back into HQ's frame. A root path, not under `/api`. */
export const WARM_PATH = "/__warm";

/** All {@link isFramed} compares: whether the page's own window is the top one. */
interface FrameWindow {
  readonly self: unknown;
  readonly top: unknown;
}

/** What {@link navigateTop} needs of a window. */
interface NavigatingWindow {
  readonly top: { location: { href: string } } | null;
  open(url: string, target: string, features: string): unknown;
}

/**
 * Whether this page is inside a frame. Reading `top` across origins can throw
 * in some browsers, which itself means framed.
 *
 * @param win - The window to ask; the real one outside tests.
 * @returns True when another page is drawing this one.
 */
export function isFramed(win: FrameWindow = window): boolean {
  try {
    return win.self !== win.top;
  } catch {
    return true;
  }
}

/**
 * Where `/api/signin` should send a framed page once Google is done: through
 * `/__warm`, which reopens `page` inside HQ's frame.
 *
 * @param page - Site-relative path and query to come back to.
 * @returns A site-relative path, which is all `/api/signin` will accept.
 *
 * @example
 * warmReturnPath("/review?po=4417"); // "/__warm?path=%2Freview%3Fpo%3D4417"
 */
export function warmReturnPath(page: string): string {
  return `${WARM_PATH}?${new URLSearchParams({ path: page }).toString()}`;
}

/**
 * Sends the whole tab to `url`. Only valid while handling a click: browsers
 * refuse a frame's top-level navigation without one. If the browser still
 * refuses, a new tab signs in instead, and comes back into HQ there.
 *
 * @param url - An absolute URL, so it means the same thing to the top window.
 * @param win - The window to navigate from; the real one outside tests.
 */
export function navigateTop(url: string, win: NavigatingWindow = window): void {
  try {
    if (!win.top) throw new Error("no top window");
    win.top.location.href = url;
  } catch {
    win.open(url, "_blank", "noopener");
  }
}
